import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Apple, Monitor, Terminal } from 'lucide-react';
import { clsx } from 'clsx';
import { SectionLabel } from '@/components/marketing/v2/SectionLabel';
import { CopyButton } from '@/components/marketing/v2/CopyButton';
import { DESKTOP_PACKAGES, type DetectedOs, detectOs } from '@/components/marketing/download/v2/shared';

type OsKey = 'mac' | 'windows' | 'linux';

type Step = {
  tag?: string;
  title: string;
  body?: ReactNode;
  command?: string;
  note?: ReactNode;
};

const fileFor = (key: string): string =>
  DESKTOP_PACKAGES.find((p) => p.key === key)?.file ?? '';

const MAC_ARM = fileFor('mac-dmg-arm64');
const MAC_INTEL = fileFor('mac-dmg-intel');
const WIN_EXE = fileFor('win-exe');
const WIN_MSI = fileFor('win-msi');
const LINUX_APPIMAGE = fileFor('linux-appimage');
const LINUX_DEB = fileFor('linux-deb');
const LINUX_RPM = fileFor('linux-rpm');

const APP_PATH = '/Applications/Velko POS.app';

const TABS: ReadonlyArray<{ key: OsKey; label: string; Icon: typeof Monitor }> = [
  { key: 'mac', label: 'macOS', Icon: Apple },
  { key: 'windows', label: 'Windows', Icon: Monitor },
  { key: 'linux', label: 'Linux', Icon: Terminal },
];

const GUIDES: Record<OsKey, ReadonlyArray<Step>> = {
  mac: [
    {
      title: 'Télécharge le bon .dmg',
      body: (
        <>
          <strong className="font-semibold text-charcoal-800">Apple Silicon</strong> (M1 à M4)&nbsp;:{' '}
          <code className="font-marketing-mono text-[13px] text-charcoal-800">{MAC_ARM}</code>
          <br />
          <strong className="font-semibold text-charcoal-800">Intel</strong>&nbsp;:{' '}
          <code className="font-marketing-mono text-[13px] text-charcoal-800">{MAC_INTEL}</code>
        </>
      ),
    },
    {
      title: 'Ouvre le .dmg et glisse l\'app dans Applications',
      body: 'Double-clique le fichier téléchargé, puis fais glisser « Velko POS » dans le dossier Applications.',
    },
    {
      title: 'Au 1er lancement, contourne Gatekeeper',
      body: (
        <>
          macOS affiche «&nbsp;<em>impossible d'ouvrir car le développeur ne peut pas être vérifié</em>
          &nbsp;». Le plus simple&nbsp;: <strong className="font-semibold text-charcoal-800">clic
          droit sur l'app → Ouvrir → Ouvrir</strong>.
        </>
      ),
    },
    {
      title: 'Le clic droit ne suffit pas ? Une commande règle tout',
      body: 'Ouvre le Terminal et colle cette commande pour retirer la mise en quarantaine, puis relance l\'app :',
      command: `xattr -d com.apple.quarantine "${APP_PATH}"`,
      note: (
        <>
          Si l'erreur persiste, essaie&nbsp;:{' '}
          <code className="font-marketing-mono text-[12.5px] text-charcoal-700">
            xattr -cr "{APP_PATH}"
          </code>
        </>
      ),
    },
  ],
  windows: [
    {
      title: 'Choisis ton mode d\'installation',
      body: (
        <>
          <strong className="font-semibold text-charcoal-800">Installeur direct (.exe ou .msi)</strong>&nbsp;:{' '}
          <code className="font-marketing-mono text-[13px] text-charcoal-800">{WIN_EXE}</code>
          {' ou '}
          <code className="font-marketing-mono text-[13px] text-charcoal-800">{WIN_MSI}</code>
          <br />
          <strong className="font-semibold text-charcoal-800">Archive d'installation (.zip)</strong>&nbsp;:
          contient l'installeur .exe et un script pour le lancer. Télécharge ces fichiers depuis la page officielle des versions.
        </>
      ),
    },
    {
      title: 'Installation avec l’archive .zip',
      body: (
        <>
          Décompresse le fichier .zip téléchargé, puis double-clique sur{' '}
          <code className="font-marketing-mono text-[13px] text-charcoal-800">installer.bat</code>.
          Le script lance l'installeur sans modifier les certificats de confiance de Windows.
        </>
      ),
    },
    {
      title: 'Si Windows affiche une alerte SmartScreen',
      body: (
        <>
          Une alerte peut apparaître malgré la signature actuelle. Vérifie que le fichier vient de la page officielle des versions.
          Si tu souhaites poursuivre, sélectionne{' '}
          <strong className="font-semibold text-charcoal-800">« Informations complémentaires »</strong>, puis{' '}
          <strong className="font-semibold text-charcoal-800">« Exécuter quand même »</strong>.
        </>
      ),
    },
    {
      title: 'Termine l\'installation',
      body: 'Suis les instructions de l\'assistant, puis lance Velko POS depuis le menu Démarrer.',
    },
  ],
  linux: [
    {
      tag: '.AppImage · portable',
      title: 'Rends le fichier exécutable, puis lance-le',
      body: 'Aucune installation requise. Télécharge l\'AppImage, place-toi dans le dossier, et exécute :',
      command: `chmod +x ${LINUX_APPIMAGE} && ./${LINUX_APPIMAGE}`,
    },
    {
      tag: '.deb · Debian / Ubuntu',
      title: 'Installe via apt',
      body: 'Depuis le dossier de téléchargement :',
      command: `sudo apt install ./${LINUX_DEB}`,
    },
    {
      tag: '.rpm · Fedora / RHEL',
      title: 'Installe via dnf',
      body: 'Depuis le dossier de téléchargement :',
      command: `sudo dnf install ./${LINUX_RPM}`,
    },
  ],
};

function CommandBlock({ command }: { command: string }) {
  return (
    <div className="mt-3 flex items-center justify-between gap-3 border-2 border-charcoal-800 bg-charcoal-900 px-4 py-3">
      <code className="overflow-x-auto whitespace-pre font-marketing-mono text-[12.5px] leading-relaxed text-lime-400">
        {command}
      </code>
      <CopyButton value={command} />
    </div>
  );
}

export default function InstallOsGuideV2() {
  const [active, setActive] = useState<OsKey>('mac');
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    const detected: DetectedOs = detectOs();
    setActive(detected === 'other' ? 'windows' : detected);
  }, []);

  const onKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const dir = e.key === 'ArrowRight' ? 1 : -1;
    const next = (idx + dir + TABS.length) % TABS.length;
    const nextTab = TABS[next];
    if (!nextTab) return;
    setActive(nextTab.key);
    tabRefs.current[next]?.focus();
  };

  return (
    <section id="install-os-guide" className="scroll-mt-24 bg-paper pb-20 md:pb-28">
      <div className="mx-auto max-w-4xl px-6 md:px-8">
        <SectionLabel>Choisis ton système</SectionLabel>
        <h2 className="mt-3 font-marketing-display text-[clamp(30px,4vw,52px)] font-bold leading-[1.02] tracking-[-0.035em] text-charcoal-800">
          Les étapes, <span className="underline-lime">pas à pas.</span>
        </h2>

        {/* Segmented OS switcher */}
        <div
          role="tablist"
          aria-label="Système d'exploitation"
          className="mt-9 inline-flex border-2 border-charcoal-800 bg-white p-1 shadow-neobrutal-4"
        >
          {TABS.map(({ key, label, Icon }, idx) => {
            const isActive = active === key;
            return (
              <button
                key={key}
                ref={(el) => {
                  tabRefs.current[idx] = el;
                }}
                role="tab"
                type="button"
                id={`os-tab-${key}`}
                aria-selected={isActive}
                aria-controls={`os-panel-${key}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActive(key)}
                onKeyDown={(e) => onKeyDown(e, idx)}
                className={clsx(
                  'inline-flex items-center gap-2 px-4 py-2.5 font-marketing-display text-[14px] font-bold tracking-[-0.01em] transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lime-500 focus-visible:ring-inset md:px-6 md:text-[15px]',
                  isActive ? 'bg-charcoal-800 text-white' : 'text-charcoal-600 hover:text-charcoal-900',
                )}
              >
                <Icon className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden="true" />
                {label}
              </button>
            );
          })}
        </div>

        {/* Panels */}
        {TABS.map(({ key }) => (
          <div
            key={key}
            role="tabpanel"
            id={`os-panel-${key}`}
            aria-labelledby={`os-tab-${key}`}
            hidden={active !== key}
            className="mt-8"
          >
            <ol className="space-y-4">
              {GUIDES[key].map((step, i) => (
                <li
                  key={step.title}
                  className="flex gap-4 border-2 border-charcoal-800 bg-white p-5 shadow-neobrutal-4 md:p-6"
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center border-2 border-charcoal-800 bg-lime-500 font-marketing-display text-[16px] font-bold text-charcoal-800"
                    aria-hidden="true"
                  >
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    {step.tag && (
                      <span className="mb-2 inline-block border-[1.5px] border-charcoal-800 bg-paper px-2 py-[2px] font-marketing-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-charcoal-700">
                        {step.tag}
                      </span>
                    )}
                    <h3 className="font-marketing-display text-[17px] font-bold leading-[1.2] tracking-[-0.01em] text-charcoal-800 md:text-[18px]">
                      {step.title}
                    </h3>
                    {step.body && (
                      <p className="mt-2 font-marketing-body text-[14.5px] leading-[1.55] text-charcoal-600">
                        {step.body}
                      </p>
                    )}
                    {step.command && <CommandBlock command={step.command} />}
                    {step.note && (
                      <p className="mt-2.5 font-marketing-body text-[13px] leading-[1.5] text-charcoal-500">
                        {step.note}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </section>
  );
}
